import React from 'react';
import PropTypes from 'prop-types';
import Field from 'zignal-landscape/lib/Field';
import Label from 'zignal-landscape/lib/Label';
import Input from 'zignal-landscape/lib/Input';
import Select from 'zignal-landscape/lib/Select';
import ErrorMessage from 'zignal-landscape/lib/ErrorMessage';
import PhoneInput from '../components/PhoneInput/PhoneInput';
import { formatTimezoneLabel } from '../userFormRules';

const TextField = ({ name, label, values, errors, onChange, cy, type = 'text', disabled = false, hint = null, autoComplete }) => (
  <Field>
    <Label htmlFor={`user-form-${name}`}>{label}</Label>
    <Input
      id={`user-form-${name}`}
      data-cy={cy}
      type={type}
      value={values[name] || ''}
      disabled={disabled}
      autoComplete={autoComplete}
      onChange={(e) => onChange(name, e.target.value)}
    />
    {hint}
    {errors[name] && <ErrorMessage>{errors[name]}</ErrorMessage>}
  </Field>
);

TextField.propTypes = {
  name: PropTypes.string.isRequired, label: PropTypes.string.isRequired, values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, cy: PropTypes.string.isRequired,
  type: PropTypes.string, disabled: PropTypes.bool, hint: PropTypes.node, autoComplete: PropTypes.string,
};

const ProfileFields = ({
  values, errors, onChange, onPhoneChange, isNew, timezones, showVerification, verified, onVerifyNow,
  phoneIsSaved = false, isVerifying = false, verifyError = null,
}) => {
  const timezoneOptions = timezones.map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }));
  const timezoneValue = timezoneOptions.find((o) => o.value === values.timezone) || null;
  const hasPhone = !!(values.phone || '').trim();

  return (
    <div>
      <TextField
        name="username" label="Username" cy="user-form-username" values={values} errors={errors} onChange={onChange}
        disabled={!isNew} hint={!isNew ? <div className="explain-input">Usernames cannot be changed</div> : null}
      />
      <TextField name="email" label="Email" cy="user-form-email" values={values} errors={errors} onChange={onChange} />

      <Field>
        <Label htmlFor="user-form-phone">Phone Number</Label>
        <PhoneInput id="user-form-phone" value={values.phone} onChange={onPhoneChange} error={errors.phone || null} />
        {/* The badge describes the saved number, so it hides while a different one is typed. */}
        {showVerification && hasPhone && phoneIsSaved && (
          verified
            ? <span data-cy="user-form-phone-verified">Verified</span>
            : (
              <span data-cy="user-form-phone-unverified">
                Not verified · <a
                  href="#verify"
                  data-cy="user-form-verify-now"
                  aria-disabled={isVerifying ? 'true' : undefined}
                  onClick={(e) => { e.preventDefault(); if (!isVerifying) { onVerifyNow(); } }}
                >{isVerifying ? 'Sending…' : 'Verify now'}</a>
              </span>
            )
        )}
        {verifyError && <div data-cy="user-form-verify-error"><ErrorMessage>{verifyError}</ErrorMessage></div>}
        <div className="explain-input">
          <div>By adding a phone number you consent to receive SMS via the contact information provided,</div>
          <div id="ph-usage">your phone number will only be used for SMS notifications. Standard rates may apply.</div>
        </div>
      </Field>

      <TextField name="first_name" label="First Name" cy="user-form-first-name" values={values} errors={errors} onChange={onChange} />
      <TextField name="last_name" label="Last Name" cy="user-form-last-name" values={values} errors={errors} onChange={onChange} />
      <TextField name="title" label="Job Title" cy="user-form-title" values={values} errors={errors} onChange={onChange} autoComplete="off" />

      <Field>
        <Label htmlFor="user-form-timezone">Timezone</Label>
        <div data-cy="user-form-timezone">
          <Select
            inputId="user-form-timezone"
            options={timezoneOptions}
            value={timezoneValue}
            isClearable={false}
            onChange={(option) => onChange('timezone', option ? option.value : '')}
          />
        </div>
        {errors.timezone && <ErrorMessage>{errors.timezone}</ErrorMessage>}
      </Field>

      <TextField
        name="password" label="Password" cy="user-form-password" type="password" autoComplete="off"
        values={values} errors={errors} onChange={onChange}
        hint={(
          <div className="explain-input">
            {!isNew && <div className="pw-changing">To change the password, please type a new one. Otherwise leave this blank.</div>}
            <div className="pw-strength">Passwords must contain at least 6 characters and a number.</div>
          </div>
        )}
      />
      <TextField
        name="confirm_password" label="Confirm Password" cy="user-form-confirm-password" type="password" autoComplete="off"
        values={values} errors={errors} onChange={onChange}
      />
    </div>
  );
};

ProfileFields.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onPhoneChange: PropTypes.func.isRequired,
  isNew: PropTypes.bool.isRequired,
  timezones: PropTypes.arrayOf(PropTypes.string).isRequired,
  showVerification: PropTypes.bool,
  verified: PropTypes.bool,
  onVerifyNow: PropTypes.func,
  phoneIsSaved: PropTypes.bool,
  isVerifying: PropTypes.bool,
  verifyError: PropTypes.string,
};

export default ProfileFields;
